import { BackIcon, ExpandIcon, ForwardIcon, ZoomInIcon, ZoomOutIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Box, Stack, useTheme } from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import UTIF from 'utif';
import { ImageProps } from './types';

type ZoomableImageProps = ImageProps & {
  showControls?: boolean;
};

export const ZoomableImage = ({
  alt,
  url,
  sx,
  overlayStyle,
  overlaySVG,
  overlayImage,
  overlayOpacity,
  onPrevClick,
  onNextClick,
  showControls,
  ...props
}: ZoomableImageProps) => {
  const { palette } = useTheme();
  const scaleUp = true;
  const zoomFactor = 8;
  const isTiff = ['tif', 'tiff'].includes(url?.split('?')?.[0]?.split('.')?.pop()?.toLowerCase());
  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
    if (!isTiff) {
      return;
    }
    const loadTiff = async () => {
      const response = await axios.get(url, {
        responseType: 'arraybuffer',
        headers: { 'Access-Control-Allow-Origin': '*' },
      });
      const ifds = UTIF.decode(response.data as Buffer);
      ifds.map((ifd, index) => {
        UTIF.decodeImage(response.data as Buffer, ifd);
        const rgba = UTIF.toRGBA8(ifd);
        const canvas = document.createElement('canvas');
        canvas.width = ifd.width;
        canvas.height = ifd.height;
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        const ctx = canvas.getContext('2d');
        const img = ctx.createImageData(ifd.width, ifd.height);
        img.data.set(rgba);
        ctx.putImageData(img, 0, 0);
        if (index === 0) document.getElementById('tiff-inner-container').replaceChildren(canvas);
        return canvas;
      });
    };
    void loadTiff();
  }, [url, isTiff]);

  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const [overlayImageError, setOverlayImageError] = useState(false);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const [imageNaturalWidth, setImageNaturalWidth] = useState<number>(0);
  const [imageNaturalHeight, setImageNaturalHeight] = useState<number>(0);

  const [imageRatio, setImageRatio] = useState<[number, number]>([1, 1]);

  const imageScale = useMemo(() => {
    if (
      containerWidth === 0 ||
      containerHeight === 0 ||
      imageNaturalWidth === 0 ||
      imageNaturalHeight === 0
    )
      return 0;
    const scale = Math.min(
      containerWidth / imageNaturalWidth,
      containerHeight / imageNaturalHeight,
    );
    return scaleUp ? scale : Math.max(scale, 1);
  }, [scaleUp, containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight]);

  const handleResize = useCallback(() => {
    if (container !== null) {
      const rect = container.getBoundingClientRect();
      setContainerWidth(rect.width);
      setContainerHeight(rect.height);
    } else {
      setContainerWidth(0);
      setContainerHeight(0);
    }
  }, [container]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const handleImageOnLoad = (image: HTMLImageElement) => {
    setImageNaturalWidth(image.naturalWidth);
    setImageNaturalHeight(image.naturalHeight);
    setImageRatio([image.naturalWidth / image.width, image.naturalHeight / image.height]);
    setImageLoaded(true);
  };

  useEffect(() => {
    if (!window) return;
    const image = new window.Image();
    image.onload = () => handleImageOnLoad(image);
    image.src = url;
  }, [url]);

  return (
    <Stack
      sx={{
        containerType: 'size',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: palette.background.default,
        width: '100%',
        height: '100%',
        position: 'relative',
        ...sx,
      }}
      ref={(el: HTMLDivElement | null) => setContainer(el)}
      {...props}
    >
      {!imageLoaded ? (
        <Box sx={{ position: 'absolute', height: '100%', aspectRatio: 1 }}>
          <svg viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M 75.392 43.104 C 75.145 42.961 74.84 42.961 74.592 43.104 L 47.576 58.712 C 47.335 58.855 47.187 59.113 47.184 59.392 L 47.184 90.592 C 47.187 90.872 47.335 91.13 47.576 91.272 L 74.608 106.896 C 74.855 107.039 75.16 107.039 75.408 106.896 L 102.424 91.288 C 102.665 91.145 102.813 90.888 102.816 90.608 L 102.816 59.408 C 102.813 59.128 102.665 58.871 102.424 58.728 L 75.392 43.104 Z M 75.8 45.128 L 100.48 59.392 L 92.296 64.113 L 75.8 54.577 L 75.8 45.128 Z M 74.2 45.128 L 74.2 54.576 L 57.704 64.112 L 49.52 59.392 L 74.2 45.128 Z M 48.744 60.744 L 56.92 65.465 L 56.92 84.537 L 48.744 89.257 L 48.744 60.744 Z M 74.2 104.864 L 49.52 90.608 L 57.704 85.888 L 74.2 95.424 L 74.2 104.864 Z M 74.2 93.664 L 58.488 84.536 L 58.488 66.368 L 74.2 75.448 L 74.2 93.664 Z M 75 74.136 L 59.264 65.016 L 75 55.928 L 90.736 65.016 L 75 74.136 Z M 91.512 66.408 L 91.512 84.536 L 75.8 93.624 L 75.8 75.448 L 91.512 66.408 Z M 75.8 104.864 L 75.8 95.424 L 92.296 85.888 L 100.48 90.609 L 75.8 104.864 Z M 101.256 89.256 L 93.08 84.536 L 93.08 65.464 L 101.256 60.744 L 101.256 89.256 Z"
              fill={palette.primary.main}
              transform="matrix(1, 0, 0, 1, 0, -3.552713678800501e-15)"
            />
            {url && (
              <animate
                attributeName="opacity"
                begin=".0s"
                values="1;0;1;1"
                dur="4s"
                repeatCount="indefinite"
              />
            )}
          </svg>
        </Box>
      ) : !imageError ? (
        <TransformWrapper
          key={`${containerWidth}x${containerHeight}`}
          initialScale={imageScale}
          minScale={imageScale}
          maxScale={imageScale * zoomFactor}
          centerOnInit
          panning={{ disabled: false, excluded: ['onContextMenu'] }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              {showControls && (
                <Stack sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2 }} gap={2}>
                  <IconButton
                    Icon={ZoomInIcon}
                    size={18}
                    onClick={() => zoomIn()}
                    sx={{
                      backgroundColor: palette.background.default,
                    }}
                  />
                  <IconButton
                    Icon={ZoomOutIcon}
                    size={18}
                    onClick={() => zoomOut()}
                    sx={{
                      backgroundColor: palette.background.default,
                    }}
                  />
                  <IconButton
                    Icon={ExpandIcon}
                    size={18}
                    onClick={() => resetTransform()}
                    sx={{
                      backgroundColor: palette.background.default,
                    }}
                  />
                </Stack>
              )}
              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                }}
              >
                {url && !isTiff && (
                  <img
                    style={{
                      display: 'block',
                      maxHeight: '100%',
                      maxWidth: '100%',
                      margin: 'auto',
                      width: 'auto',
                      height: '100%',
                      backgroundColor: palette.background.default,
                      visibility: imageLoaded ? 'visible' : 'hidden',
                      zIndex: 1,
                    }}
                    onError={() => setImageError(true)}
                    onContextMenu={(e) => e.stopPropagation()}
                    src={url}
                    alt={alt || 'image'}
                  />
                )}
                {url && isTiff && (
                  <div
                    id="tiff-inner-container"
                    style={{
                      display: 'block',
                      maxHeight: '100%',
                      maxWidth: '100%',
                      margin: 'auto',
                      width: 'auto',
                      height: '100%',
                      backgroundColor: palette.background.default,
                      zIndex: 1,
                    }}
                  />
                )}
                {imageLoaded && overlaySVG && (
                  <svg
                    style={{
                      position: 'absolute',
                      inset: 0,
                      height: '100%',
                      width: '100%',
                      opacity: overlayOpacity ?? 1,
                      pointerEvents: 'none',
                      zIndex: 2,
                      ...overlayStyle,
                    }}
                    viewBox={`0 0 ${imageNaturalWidth} ${imageNaturalHeight}`}
                  >
                    {overlaySVG([imageNaturalWidth, imageNaturalHeight], imageRatio)}
                  </svg>
                )}
                {imageLoaded && overlayImage && !overlayImageError && (
                  <img
                    style={{
                      position: 'absolute',
                      inset: 0,
                      height: '100%',
                      width: '100%',
                      opacity: overlayOpacity ?? 0.5,
                      pointerEvents: 'none',
                      ...overlayStyle,
                    }}
                    src={overlayImage}
                    onError={() => setOverlayImageError(true)}
                    alt={alt ? `${alt} overlay` : 'overlay image'}
                  />
                )}
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      ) : null}
      {onPrevClick && (
        <IconButton
          Icon={BackIcon}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            backgroundColor: palette.background.default,
          }}
          onClick={onPrevClick}
          size={24}
        />
      )}
      {onNextClick && (
        <IconButton
          Icon={ForwardIcon}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            backgroundColor: palette.background.default,
          }}
          onClick={onNextClick}
          size={24}
        />
      )}
    </Stack>
  );
};
